import { Portal } from '@angular/cdk/portal';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatDrawerMode } from '@angular/material/sidenav';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import type { Breadcrumbs } from '@vendasta/uikit';
import {
  asyncScheduler,
  combineLatest,
  filter,
  iif,
  map,
  Observable,
  observeOn,
  of,
  startWith,
  Subscription,
  switchMap,
} from 'rxjs';
import { partnerId } from '../../globals';
import { AccountGroupService } from '../account-group';
import { AppConfigService } from '../app-config.service';
import { showAuthDialog } from '../auth-wall/auth-wall-dialog.component';
import { fakeHasCustomIDPForVBC } from '../auth-wall/auth-wall.service';
import { AuthService } from '../auth.service';
import { PartnerService } from '../core';
import { ImageService } from '../core/image.service';
import { LocationsService } from '../locations';
import { PageAccessService, PageId } from '../page-access';
import { WorkOrderStatusBadgeService } from '../work-order/status-badge.service';
import { NavigationService } from './navigation.service';
import { Mode, SidepanelService, Size, SizePixels } from './sidepanel.service';

@Component({
  selector: 'bc-nav',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  standalone: false,
})
export class NavigationComponent implements OnInit, AfterViewInit, OnDestroy {
  partnerId = partnerId;
  private readonly authService = inject(AuthService);
  user$ = this.authService.currentUser$;
  readonly breadcrumbs: Signal<Breadcrumbs[]> = toSignal(this.navigationService.breadcrumbs$);
  @ViewChild('inject', { read: ViewContainerRef })
  injectRef?: ViewContainerRef;
  sidenavMode: MatDrawerMode = 'side';
  private componentRef?: ComponentRef<unknown>;
  sidePanelSize = Size.REGULAR;
  sidePanelOpened = false;
  sidePanelMode = Mode.OVER;
  toolbarRightDistance = '0px';
  hideNavToolbar$: Observable<boolean> = this.navigationService?.hideNavToolbar$;
  private previousUrl: string;
  private subscriptions = [] as Subscription[];
  // Atlas Configs
  topGap = 40;
  readonly breadcrumbPortalContent = signal<Portal<unknown>>(null);
  readonly pageActionsPortalContent = signal<Portal<unknown>>(null);

  isWorkOrderDetailsVisible$ = this.pageVisibilityService?.isPageAccessible$(PageId.work_order);

  showAuthDialog = showAuthDialog();
  displayDateSelector$ = this.navigationService.displayDateSelector$;
  displayPrintButton$ = this.navigationService.displayPrintButton$;
  displayComposeButton$ = this.navigationService.displayComposeButton$;
  displayFilterContainer$ = this.navigationService.displayFilterContainer$;
  displayFilterToggle$ = this.navigationService.displayFilterButton$;

  actionButtons$ = this.navigationService.actionButtons$;
  workOrderBadgeTemplate$ = this.isWorkOrderDetailsVisible$.pipe(
    startWith(false),
    switchMap((isWorkOrderDetailsVisible) =>
      iif(() => isWorkOrderDetailsVisible, this.workOrderStatusBadgeService.badgeTemplate$, of(null)),
    ),
  );

  bannerAuthSignupIsEnabled$ = fakeHasCustomIDPForVBC(partnerId).pipe(map((enabled) => !enabled));
  currentLocation$ = this.locationsService.currentLocation$;
  isCurrentLocationABrand$ = this.locationsService.isCurrentLocationABrand$;
  partnerName$ = this.partnerService.partnerBrandName$;

  constructor(
    public dialog: MatDialog,
    public accountGroupService: AccountGroupService,
    public appConfig: AppConfigService,
    public pageVisibilityService: PageAccessService,
    public navigationService: NavigationService,
    private title: Title,
    public router: Router,
    public locationsService: LocationsService,
    private partnerService: PartnerService,
    public sidepanelService: SidepanelService,
    private changeDetectorRef: ChangeDetectorRef,
    private translateService: TranslateService,
    public imageService: ImageService,
    private workOrderStatusBadgeService: WorkOrderStatusBadgeService,
  ) {
    this.navigationService.breadcrumbs$
      .pipe(
        filter((breadcrumbs) => breadcrumbs?.length > 0),
        map((breadcrumbs) => {
          return this.translateService.instant(breadcrumbs[0]?.text);
        }),
        takeUntilDestroyed(),
      )
      .subscribe((pageTitle) => {
        this.title.setTitle(pageTitle);
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd || event instanceof NavigationStart),
        takeUntilDestroyed(),
      )
      .subscribe((event: NavigationEnd | NavigationStart) => {
        if (event instanceof NavigationStart) {
          this.previousUrl = window.location.pathname;
        } else if (this.previousUrl !== event.url.split('?')[0]) {
          this.toolbarRightDistance = '0px';
        }
      });
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    if (this.isMobile()) {
      this.sidenavMode = 'over';
    } else {
      this.sidenavMode = 'side';
    }
  }

  ngOnInit(): void {
    this.setupSidepanel();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.changeDetectorRef.detectChanges();
  }

  print(): void {
    window.print();
  }

  ngAfterViewInit(): void {
    this.subscriptions.push(
      combineLatest([this.sidepanelService.view$, this.sidepanelService.visible$])
        .pipe(observeOn(asyncScheduler))
        .subscribe(([c, visible]) => {
          // If null is passed in, that means the view should be removed.
          if (!c || !visible) {
            // Remove old component from drawer
            this.injectRef?.remove();
            return;
          }
          if (this.componentRef) {
            // Remove old component from drawer
            this.injectRef?.remove();
          }
          // Draw new component
          this.componentRef = this.injectRef.createComponent(c);
        }),
    );
    this.changeDetectorRef.detectChanges();
  }

  private setupSidepanel(): void {
    this.subscriptions.push(
      this.sidepanelService.visible$.pipe(observeOn(asyncScheduler)).subscribe((visible) => {
        this.sidePanelOpened = visible;
      }),
    );
    this.subscriptions.push(
      combineLatest([this.sidepanelService.mode$, this.sidepanelService.size$, this.sidepanelService.visible$])
        .pipe(observeOn(asyncScheduler))
        .subscribe(([mode, size, visible]) => {
          this.sidePanelMode = mode;
          this.sidePanelSize = size;
          if (mode === Mode.SIDE && visible) {
            if (size === Size.SMALL) {
              this.toolbarRightDistance = `${SizePixels.SMALL}px`;
            } else if (size === Size.REGULAR) {
              this.toolbarRightDistance = `${SizePixels.REGULAR}px`;
            } else {
              this.toolbarRightDistance = `${SizePixels.LARGE}px`;
            }
          } else {
            this.toolbarRightDistance = '0px';
          }
          this.changeDetectorRef.detectChanges();
        }),
    );

    if (this.isMobile()) {
      this.sidenavMode = 'over';
    }
  }

  isMobile(): boolean {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  get showMyListingEditButton(): boolean {
    return this.router.url.substring(this.router.url.lastIndexOf('/') + 1) === PageId.website;
  }

  toggleFilter(): void {
    this.sidepanelService.toggle();
  }

  goToLogin(): void {
    this.showAuthDialog();
  }

  goToSignUp(): void {
    this.showAuthDialog(true);
  }

  get bannerAuthIsEnabled(): boolean {
    if (!this.authService.isInPreviewMode()) {
      return false;
    }
    const isUnderPath = ['store', 'review-cart', 'orders'].some((path) => window.location.href.indexOf(path) >= 0);
    return !isUnderPath;
  }

  closeSideNav(): void {
    this.sidepanelService.close();
  }
}
